import React from "react";
import { Flex, type BoxProps, SimpleGrid, Text, type TextProps, Box } from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";
import { type Customer } from "lib/sanity";
import Balancer from "react-wrap-balancer";
import { Section, type SectionProps } from "components/section";
import { Button } from "ui/button";
import { getCustomerLogoUrl, getCustomerLogoStyles, mobileOnlyDisplay, desktopOnlyDisplay } from "utils";
import { trackCustomerLogosClicked } from "components/event-tracking";

type StackedLogosProps = {
  /** If true, the logos will be wrapped in a link to the /customers page with a hover effect. */
  hasLink?: boolean;
  title?: string;
  titleMaxW?: TextProps["maxW"];
  customers: Customer[];
  /** If not provided, the first 8 logos of `customers` will be used as mobile logos. */
  mobileCustomers?: Customer[];
} & Pick<SectionProps, "theme" | "themeBgColor"> &
  BoxProps;

/**
 * The default logo display block, which is designed to support either 8 or 16 `customers`.
 *
 * On desktop, this will render all the `customers` logos in rows of 8 logos each.
 * On mobile, this is designed to display exactly 8 logos (in two rows of 4).
 */
export function StackedLogos({
  hasLink = true,
  title = "The world’s most innovative companies choose Hightouch ",
  titleMaxW = "4xl",
  customers,
  mobileCustomers,
  theme = "light",
  themeBgColor = "white",
  ...props
}: StackedLogosProps) {
  if (customers.length === 0) {
    return null;
  }

  const mobileLogos = mobileCustomers && mobileCustomers.length > 0 ? mobileCustomers : customers.slice(0, 8);

  const linkProps = hasLink ? { "aria-label": "Read case studies", className: "group", href: "/customers" } : {};

  const handleLogoClick = () => {
    if (hasLink) {
      trackCustomerLogosClicked("stacked-logos");
    }
  };

  return (
    <Section theme={theme} themeBgColor={themeBgColor} {...props}>
      {title && (
        <Text
          color={`brand.text.${theme}`}
          textStyle={{ base: "headline3", md: "headline2" }}
          textAlign="center"
          maxW={titleMaxW}
          mx="auto"
          mb={{ base: "bigger", md: "biggest" }}
        >
          <Balancer>{title}</Balancer>
        </Text>
      )}
      <SimpleGrid
        as={hasLink ? Link : "div"}
        display={mobileOnlyDisplay("grid")}
        columns={4}
        alignItems="center"
        spacing="big"
        maxW={{ base: "100%", sm: "90%" }}
        mx="auto"
        _focus={{ outline: "none !important" }}
        _focusVisible={{
          outline: "none !important",
          boxShadow: "outline",
        }}
        onClick={handleLogoClick}
        {...linkProps}
      >
        {mobileLogos.filter(Boolean).map((customer: Customer) => (
          <Flex
            key={customer.slug}
            pos="relative"
            align="center"
            justify="center"
            maxW="100%"
            w="100%"
            userSelect="none"
            {...getCustomerLogoStyles(theme)}
          >
            <Image
              src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
              alt={`${customer.name}.`}
              sizes="100%"
              width={5}
              height={2}
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "100%",
                height: "unset",
                minWidth: "60px",
                maxWidth: "100px",
                maxHeight: "36px",
              }}
            />
          </Flex>
        ))}
      </SimpleGrid>

      <Box
        as={hasLink ? Link : "div"}
        display={desktopOnlyDisplay("grid")}
        pos="relative"
        _focus={{ outline: "none !important" }}
        _focusVisible={{
          outline: "none !important",
        }}
        onClick={handleLogoClick}
        {...linkProps}
      >
        <SimpleGrid
          columns={{ base: 4, lg: 8 }}
          alignItems="center"
          spacing="big"
          w="100%"
          maxW={{ base: "3xl", lg: "100%" }}
          mx="auto"
          transition="0.25s ease-out"
          transitionProperty="filter, opacity"
          _groupHover={{ filter: "blur(4px)", opacity: 0.7 }}
          _groupFocusVisible={{ filter: "blur(4px)", opacity: 0.7 }}
        >
          {customers.filter(Boolean).map((customer: Customer) => (
            <Flex
              key={customer.slug}
              pos="relative"
              align="center"
              justify="center"
              w="100%"
              maxW="100%"
              userSelect="none"
              {...getCustomerLogoStyles(theme)}
            >
              <Image
                src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
                alt={`${customer.name}.`}
                sizes="100%"
                width={5}
                height={2}
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                  width: "100%",
                  height: "unset",
                  minWidth: "60px",
                  maxWidth: "100px",
                  maxHeight: "36px",
                }}
              />
            </Flex>
          ))}
        </SimpleGrid>

        {hasLink && (
          <Flex
            align="center"
            justify="center"
            pos="absolute"
            inset={0}
            opacity={0}
            transform="scale(0.95)"
            transition="0.25s ease-out"
            transitionProperty="opacity, transform"
            _groupHover={{ opacity: 1, transform: "scale(1)" }}
            _groupFocusVisible={{ opacity: 1, transform: "scale(1)" }}
          >
            <Button
              hasRightChevron
              as="div"
              href="/customers"
              variant="secondary"
              size="sm"
              // Override default border color so it always matches the hover state.
              borderColor="brand.textSecondary.light"
              _groupFocusVisible={{ boxShadow: "outline" }}
            >
              Read case studies
            </Button>
          </Flex>
        )}
      </Box>
    </Section>
  );
}
